/**
 * IN SHORT: This is the 'Dictionary' and 'Blueprint' file. It defines the core data structures,
 * configuration options, and hardcoded security constants used by the entire SDK.
 */
import type { JWK, JWTPayload } from 'jose';

// ─── Algorithm constants ───────────────────────────────────────────────────
// Pinned at the type level. Never accept algorithm from untrusted input.

export const SIGNING_ALG = 'RS256' as const;
export const KEY_WRAP_ALG = 'RSA-OAEP-256' as const;
export const CONTENT_ENC_ALG = 'A256GCM' as const;
export const RSA_KEY_SIZE = 2048 as const;

export type SigningAlg = typeof SIGNING_ALG;
export type KeyWrapAlg = typeof KEY_WRAP_ALG;
export type ContentEncAlg = typeof CONTENT_ENC_ALG;

// ─── Key types ────────────────────────────────────────────────────────────

/**
 * A client RSA key pair. The private key is non-extractable — it cannot be
 * serialised to bytes by JavaScript code, only used for sign/decrypt ops.
 * Only the public JWK is serialisable (for sending to the server).
 */
export interface ClientKeyPair {
  /**
   * Non-extractable private key usable for:
   * - RS256 signing  (requires import with 'RSASSA-PKCS1-v1_5')
   * - RSA-OAEP decryption (requires import with 'RSA-OAEP')
   *
   * ARCHITECTURE DECISION: We store TWO CryptoKey handles derived from the
   * same JWK material — one per algorithm — because the Web Crypto API binds
   * keys to a single algorithm at import time. This is a browser limitation,
   * not a security issue. The underlying RSA key material is identical.
   */
  signingKey: CryptoKey;     // RSASSA-PKCS1-v1_5, non-extractable
  decryptionKey: CryptoKey;  // RSA-OAEP, non-extractable
  publicKeyJwk: JWK;         // exportable public key for server registration
  keyId: string;             // kid
  createdAt: number;         // Unix epoch seconds
  expiresAt?: number;        // Unix epoch seconds — used for rotation
}

/**
 * Server public keys fetched from the JWKS endpoint.
 * These CryptoKey objects ARE extractable because they are public information.
 */
export interface ServerPublicKeys {
  verificationKey: CryptoKey;  // RSASSA-PKCS1-v1_5 — verify server responses
  encryptionKey: CryptoKey;    // RSA-OAEP — encrypt requests to server
  keyId?: string;
  fetchedAt: number;
  expiresAt?: number;
}

// ─── JWT claims ───────────────────────────────────────────────────────────

export interface SecurityClaims {
  jti?: string;    // JWT ID — replay prevention
  nonce?: string;  // Request nonce — extra replay prevention layer
  kid?: string;    // Signing key ID
}

export type AppPayload<T extends Record<string, unknown> = Record<string, unknown>> =
  JWTPayload & SecurityClaims & T;

// ─── SDK configuration ────────────────────────────────────────────────────

/**
 * Resolved config consumed by buildRequestToken / parseResponseToken.
 * Sealed within the SDK; callers construct this directly or pick the subset
 * each function needs.
 */
export interface ResolvedConfig {
  /** `iss` claim on outbound requests AND expected `aud` on inbound responses. */
  issuer?: string;
  /** `aud` claim on outbound requests. */
  audience?: string;
  /**
   * Expected `iss` claim on inbound server responses. If set, responses with
   * a different `iss` are rejected. Strongly recommended in production —
   * without it, a leaked verification key authenticates any issuer.
   */
  serverIssuer?: string;
  /** Token TTL in seconds applied at signing. */
  tokenTtlSeconds: number;
  /** Symmetric clock-skew tolerance applied on verification. */
  clockSkewSeconds: number;
  /** When true, parseResponseToken records JTIs and rejects duplicates. */
  enableReplayPrevention: boolean;
}
